import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from "react-router-dom";
import ActiveHelper from '../../../components/common/activeHelper';

import { firestore } from '../../../helpers/Firebase';
import { createObjectsFromArray, deviceWidth, getCollectionName, sortObjectByCategory } from '../../../helpers/Utils';
import { SetActiveItem } from '../../../redux/activeItem/actions';
import RecoveryPricing from './components/recoveryPricing';
import { accessTypes } from '../../../helpers/Utils';
import QuickWashPricing from './components/quickWashPricing';

const Services = ({ match }) => {
  const { activeItem, } = useSelector(state => state.activeItem);
  const { services, advServices, modifiers } = useSelector(state => state.providers);
  const [externalServices, setExternalServices] = useState([])
  const { user } = useSelector(state => state.authUser);
  const search = useSelector(state => state.settings.search) || '';
  const dispatch = useDispatch();

  useEffect(() => {
    async function fetchData() {
      let externalServices = await firestore().collection('externalServices').get()
      let externalServicesData = {}
      externalServices.forEach(doc => {
        externalServicesData[doc.id] = { key: doc.id, ...doc.data(), external: true }
      })
      setExternalServices(externalServicesData)
    }
    fetchData();
  }, [])

  // Filter data based on search term
  const filteredData = useMemo(() => {
    const allServices = Object.values(services || {}).concat(Object.values(advServices || {})).concat(Object.values(externalServices || {}));
    
    if (!search || !search.trim()) {
      return allServices;
    }

    const searchLower = search.toLowerCase().trim();
    return allServices.filter(service => {
      const nameMatch = (service?.name || '')?.toLowerCase().includes(searchLower);
      const nameArMatch = (service?.name_ar || '')?.toLowerCase().includes(searchLower);
      return nameMatch || nameArMatch;
    });
  }, [services, advServices, externalServices, search]);
  const elements = useMemo(() => {
    // Helper functions to check service type
    const isPPFService = () => {
      const serviceType = activeItem?.general?.type?.toLowerCase() || '';
      return serviceType === 'ppf';
    };

    const isWindowTintService = () => {
      return activeItem?.general?.advservice && !isPPFService();
    };

    const isPPFOrAdvService = () => {
      return isPPFService() || isWindowTintService();
    };

    return [{
      key: 'generalInfo',
      title: 'General Information',
      data: [
        { key: 'Service_image', title: 'Service Image', width: '6', type: 'picture', dimensions: { width: 1080, height: 1440 } },
        { key: 'image', title: 'Home Screen Image', width: '6', type: 'picture', dimensions: { width: 1080, height: 1440 } },
        { key: 'name', title: 'Name', width: '6', type: 'text' },
        { key: 'name_ar', title: 'Arabic Name', width: '6', type: 'text' },
        { key: 'modifiers', title: 'Select Modifiers', width: '12', data: Object.values(sortObjectByCategory(modifiers || {}) || {}), type: 'modifiers', hide: activeItem?.general?.type === 'recovery' || activeItem?.general?.external },
        {
          key: 'waitingTime',
          title: 'Waiting time before auto-cancel (minutes)',
          width: '12',
          type: 'number',
          hide: activeItem?.general?.type !== 'quickWash'
        },
        {
          key: 'arrivalTime',
          title: 'Arrival Time (minutes)',
          width: '12',
          type: 'number',
          hide: activeItem?.general?.type !== 'quickWash',
          description: '* Promised SP arrival time after the customer books a quick wash. Defaults to 30 minutes if empty.',
        },
        {
          key: 'beforeAfterPhotos',
          title: 'Before & after photos',
          width: '12',
          type: 'switch',
          hideTitle: true, // the switch shows its own title
          description: 'Washers are asked to take before photos on arrival and after photos on completion. Customers see them on the order.',
          hide: activeItem?.general?.type === 'recovery' || activeItem?.general?.external,
        },
        // {
        //   key: 'type', title: 'Type', width: '12', type: 'select',
        //   disabled: !activeItem?.general?.addNew,
        //   data: [
        //     { name: 'Washing Service', key: 'washingService', id: 'washingService' },
        //     { name: 'On Site Wash', key: 'onSiteWash', id: 'onSiteWash' },
        //     { name: 'Registration', key: 'registration', id: 'registration' },
        //     { name: 'Recovery', key: 'recovery', id: 'recovery' },
        //     { name: 'External', key: 'external', id: 'external' },
        //   ]
        // },

        // Window Tinting specific fields (heat levels and tint levels)
        { key: 'heatLevels', title: 'Heat Rejection Levels', width: '12', data: createObjectsFromArray(activeItem?.general?.heatLevels || []), type: 'multi-select', hide: !isWindowTintService(), formatCreateLabel: 'Create Heat Level' },
        { key: 'tintLevels', title: 'Tint Levels', width: '12', data: createObjectsFromArray(activeItem?.general?.tintLevels || []), type: 'multi-select', hide: !isWindowTintService(), formatCreateLabel: 'Create Tint Level' },
        { key: 'defaultTintLevel', title: 'Default Tint Level', width: '12', data: createObjectsFromArray(activeItem?.general?.tintLevels || []), type: 'select', hide: !isWindowTintService() },

        // Common fields for both Window Tinting and PPF
        { key: 'tintingSides', title: 'Tinting Sides', width: '12', data: activeItem?.general?.tintingSides?.map(i => ({ ...i, name: i?.name, value: i?.name, label: i?.name, key: i?.key, id: i?.key })), type: 'tintingSides', hide: !isPPFOrAdvService() },
        { key: 'brands', title: 'Brands', width: '12', data: createObjectsFromArray(activeItem?.general?.brands || []), type: 'multi-select', hide: !isPPFOrAdvService(), formatCreateLabel: 'Create Brand' },
        { key: 'madeIn', title: 'Made In Countries', width: '12', data: createObjectsFromArray(activeItem?.general?.madeIn || []), type: 'multi-select', hide: !isPPFOrAdvService(), formatCreateLabel: 'Create Made In Country' },

        // PPF specific fields (warranty and finish - modelSeries is handled per variation)
        { key: 'warranty', title: 'Warranty Options', width: '12', data: createObjectsFromArray(activeItem?.general?.warranty || []), type: 'multi-select', hide: !isPPFService(), formatCreateLabel: 'Create Warranty Option' },
        { key: 'finish', title: 'Finish Options', width: '12', data: activeItem?.general?.finish?.map(i => ({ ...i, name: i?.name, value: i?.name, label: i?.name, key: i?.key, id: i?.key })), type: 'finish', hide: !isPPFService() },

        {
          key: 'recoveryPricing', title: 'Pricing', width: '12', hideTitle: true, data: <RecoveryPricing
            defaultValue={activeItem?.general?.recoveryPricing}
            update={(e) => {
              dispatch(SetActiveItem({ ...activeItem, general: { ...activeItem?.general, recoveryPricing: e } }))
            }} />, type: 'component', hide: activeItem?.general?.type !== 'recovery'
        },
        {
          key: 'quickWashPricing', title: 'Pricing', width: '12', hideTitle: true, data: <QuickWashPricing
            defaultValue={activeItem?.general?.quickWashPricing}
            selectedModifiers={activeItem?.general?.modifiers || []}
            update={(e) => {
              dispatch(SetActiveItem({ ...activeItem, general: { ...activeItem?.general, quickWashPricing: e } }))
            }} />, type: 'component', hide: activeItem?.general?.type !== 'quickWash'
        },
      ]
    },
    {
      key: 'description',
      title: 'Description',
      hide: activeItem?.general?.type === 'recovery' || activeItem?.general?.external,
      data: [
        { key: 'description', title: 'Description', width: '12', data: createObjectsFromArray(activeItem?.general?.description || []), type: 'multi-select', formatCreateLabel: 'Add Description' },
        { key: 'description_ar', title: 'Arabic Description', width: '12', data: createObjectsFromArray(activeItem?.general?.description_ar || []), type: 'multi-select', formatCreateLabel: 'Add Arabic Description' },
      ]
    },
    {
      key: 'waterlessDescription',
      title: 'Waterless Description',
      hide: !activeItem?.general?.washingService,
      data: [
        { key: 'waterlessDescription', title: 'Waterless Description', width: '12', data: createObjectsFromArray(activeItem?.general?.waterlessDescription || []), type: 'multi-select', formatCreateLabel: 'Add Waterless Description' },
        { key: 'waterlessDescription_ar', title: 'Arabic Waterless Description', width: '12', data: createObjectsFromArray(activeItem?.general?.waterlessDescription_ar || []), type: 'multi-select', formatCreateLabel: 'Add Arabic Waterless Description' },
      ]
    },
    {
      key: 'steamWashDescription',
      title: 'Steam Wash Description',
      hide: !activeItem?.general?.washingService,
      data: [
        { key: 'steamWashDescription', title: 'Steam Wash Description', width: '12', data: createObjectsFromArray(activeItem?.general?.steamWashDescription || []), type: 'multi-select', formatCreateLabel: 'Add Steam Wash Description' },
        { key: 'steamWashDescription_ar', title: 'Arabic Steam Wash Description', width: '12', data: createObjectsFromArray(activeItem?.general?.steamWashDescription_ar || []), type: 'multi-select', formatCreateLabel: 'Add Arabic Steam Wash Description' },
      ]
    }
    ];
  }, [modifiers, activeItem, dispatch]);

  const tableColumns = useMemo(() => {
    return [
      { Header: "Status", accessor: "active", width: 120, type: 'switch' },
      { Header: "", accessor: "image", width: deviceWidth() === 'desktop' ? 150 : 80, type: 'img' },
      { Header: "Name", accessor: "name", width: 150, type: 'label' },
      { Header: "Arabic Name", accessor: "name_ar", width: 150, type: 'label' },
      { Header: 'Created', accessor: 'created', width: 150, type: 'date', show: deviceWidth() === 'desktop' },
      { Header: 'Index', accessor: 'index', type: 'label', show: false },
      { Header: 'Updated', accessor: 'updated', type: 'date', show: deviceWidth() === 'desktop' },
    ];
  }, []);

  return (
    <div className="dashboard-wrapper">
      <Switch>
        <Route path={`${match.url}/`} render={() => (
          <ActiveHelper
            data={filteredData || []}
            title="Services"
            selected={getCollectionName(activeItem?.general || {})}
            elements={elements}
            tableColumns={tableColumns}
            enableSorting={true}
            defaultSorting="index"
            sortDirection={'asc'}
            showCreateButton={accessTypes.includes(user?.accessType)}
          />
        )} />

        <Redirect to="/error" />
      </Switch>
    </div>
  )
};
export default Services;
